import {checkFieldLimits, checkProductLimits} from '../lib/ddiUnits'

// Step indices mirror `stepValues` in jobCreate.js (WorkorderStepper):
//   0 = Job Name, 1 = Grower/Farm/Field, 2 = Product Config, 3 = Field Information
// errorSteps() returns these so the stepper can expand the steps that contain errors.
const STEP = {
    WO_NAME: 0,
    PRODUCT_INFO: 2,
    SCOUT_FEATURES: 3,
}

function CreateWorkorderValidator() {
    function validateWorkorder({products, workorderName, fieldDisplayArea, fieldAreaDisplayUnit}) {
        const errors = [
            ...validateWorkorderName(workorderName),
            ...validateProducts(products),
            ...validateFieldLimits(fieldDisplayArea, fieldAreaDisplayUnit),
        ]

        return {
            isValid: errors.length === 0,
            errors,
        }
    }

    function validateWorkorderName(workorderName) {
        if (!workorderName) {
            return [error(STEP.WO_NAME, 'workorderName', 'Job Name is required.')]
        }
        if (workorderName.length > 32) {
            return [error(STEP.WO_NAME, 'workorderName', 'Job Name must be 32 characters or fewer.')]
        }

        return []
    }

    function validateProducts(products) {
        // FR-008: a product must actually be SELECTED. The Job Generator always carries a
        // default row (mixId -1), so "no product configured" means no row has a selected
        // product — not an empty array. In that case emit a single, clear message rather
        // than granular "select a product"/rate errors on the blank default row.
        const hasSelectedProduct = products && products.some(prod => prod.mixId > 0)
        if (!hasSelectedProduct) {
            return [error(STEP.PRODUCT_INFO, 'product', 'Product Config is required.')]
        }

        const errors = []
        for (const prod of products) {
            if (prod.mixId <= 0) {
                // An unselected extra row alongside a configured product.
                errors.push(error(STEP.PRODUCT_INFO, 'product', 'Product Config: select a product.'))
                continue
            }
            if (!prod.userDisplayUnit) {
                errors.push(error(STEP.PRODUCT_INFO, 'product', 'Product Config: select a unit.'))
                continue
            }
            const rateError = checkProductLimits(prod.displayRate, prod.userDisplayUnit, prod.mode === 'rxMap')
            if (rateError) {
                errors.push(error(STEP.PRODUCT_INFO, 'product', rateMessage(rateError)))
            }
        }

        return errors
    }

    function validateFieldLimits(fieldDisplayArea, fieldAreaDisplayUnit) {
        const errorText = checkFieldLimits(fieldDisplayArea, fieldAreaDisplayUnit)
        if (errorText) {
            return [error(STEP.SCOUT_FEATURES, 'fieldDisplayArea', `Field Information: ${errorText}`)]
        }

        return []
    }

    return {
        validateWorkorder,
        summaryMessages,
        errorSteps,
    }
}

function error(step, field, message) {
    return {step, field, message}
}

// checkProductLimits returns a generic 'Required Field' for a missing rate; make the
// summary message explicit about what's required. Other limit errors (Min/Max/Invalid)
// already carry their own specifics, so keep them, prefixed with "target rate".
function rateMessage(rateError) {
    if (rateError === 'Required Field') {
        return 'Product Config: a target rate is required.'
    }

    return `Product Config: target rate ${rateError}`
}

// Pure helper: ordered, de-duplicated messages for the aggregated summary.
function summaryMessages(errors) {
    const seen = new Set()
    const messages = []
    for (const {message} of errors) {
        if (!seen.has(message)) {
            seen.add(message)
            messages.push(message)
        }
    }

    return messages
}

// Pure helper: unique step indices that contain at least one error, ascending.
function errorSteps(errors) {
    return [...new Set(errors.map(e => e.step))].sort((a, b) => a - b)
}

export default CreateWorkorderValidator()
